import React, { useState } from 'react';
import { compressImageToWebP, formatFileSize } from '../../utils/mediaOptimizer.ts';
import {
  Upload,
  Image as ImageIcon,
  Film,
  Trash2,
  Plus,
  Play,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Link as LinkIcon,
  Code,
  Loader2,
} from 'lucide-react';
import { extractIframeSrc, isIframeEmbed } from '../../utils/videoUtils.ts';

interface ProductMediaUploaderProps {
  coverImage: string;
  galleryImages: string[];
  videoUrl?: string;
  videoType?: 'file' | 'youtube' | 'url';
  onUpdateCover: (url: string) => void;
  onUpdateGallery: (urls: string[]) => void;
  onUpdateVideo: (videoUrl: string, videoType: 'file' | 'youtube' | 'url') => void;
  primaryColor?: string;
}

export default function ProductMediaUploader({
  coverImage,
  galleryImages,
  videoUrl,
  videoType = 'file',
  onUpdateCover,
  onUpdateGallery,
  onUpdateVideo,
  primaryColor = '#059669',
}: ProductMediaUploaderProps) {
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressStats, setCompressStats] = useState<string | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState('');
  const [tempVideoYoutube, setTempVideoYoutube] = useState(videoType === 'youtube' ? videoUrl || '' : '');
  const [tempVideoEmbed, setTempVideoEmbed] = useState('');
  const [videoInputMode, setVideoInputMode] = useState<'upload' | 'embed' | 'youtube'>(
    videoType === 'youtube' || (videoUrl && (videoUrl.includes('youtube') || videoUrl.includes('youtu.be')))
      ? 'youtube'
      : (videoUrl && videoUrl.includes('<iframe')) ? 'embed' : 'upload'
  );
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);

  // Handle multiple photo uploads with canvas compression
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsCompressing(true);
    setCompressStats('Mengompresi foto ke format WebP hemat kuota...');

    const newOptimizedImages: string[] = [];
    let totalOriginalSize = 0;
    let totalCompressedSize = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      totalOriginalSize += file.size;

      try {
        const compressed = await compressImageToWebP(file, 1200, 0.82);
        newOptimizedImages.push(compressed.dataUrl);
        totalCompressedSize += compressed.compressedSizeBytes;
      } catch (err) {
        console.error('Failed to compress image:', err);
      }
    }

    if (newOptimizedImages.length > 0) {
      // If no cover image yet, make the first one cover
      if (!coverImage) {
        onUpdateCover(newOptimizedImages[0]);
      }
      onUpdateGallery([...galleryImages, ...newOptimizedImages]);

      const savingsPercent = Math.round(((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100);
      setCompressStats(
        `Sukses! ${newOptimizedImages.length} foto berhasil dioptimasi. Hemat ${savingsPercent}% kuota (${formatFileSize(totalCompressedSize)}).`
      );
    }

    setIsCompressing(false);
    e.target.value = '';
  };

  // Handle Video file upload to server /api/upload
  const handleVideoFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/') && !file.name.match(/\.(mp4|webm|mov|mkv|avi|m4v)$/i)) {
      alert('Format berkas harus video (MP4, WebM, MOV, MKV).');
      return;
    }

    if (file.size > 200 * 1024 * 1024) {
      alert('Ukuran video maksimal 200MB.');
      return;
    }

    setIsUploadingVideo(true);
    setVideoUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/upload');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setVideoUploadProgress(Math.min(95, Math.max(10, percent)));
        }
      };

      xhr.onload = () => {
        setIsUploadingVideo(false);
        setVideoUploadProgress(100);
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.success && data.url) {
            onUpdateVideo(data.url, 'file');
          } else {
            alert(data.message || 'Gagal mengunggah video ke server.');
          }
        } catch (err) {
          alert('Gagal memproses respon server saat upload video.');
        }
      };

      xhr.onerror = () => {
        setIsUploadingVideo(false);
        alert('Terjadi kesalahan jaringan saat mengunggah video.');
      };

      xhr.send(formData);
    } catch (err: any) {
      setIsUploadingVideo(false);
      alert(err.message || 'Gagal mengunggah video.');
    } finally {
      e.target.value = '';
    }
  };

  const handleSaveEmbed = () => {
    const trimmed = tempVideoEmbed.trim();
    if (!trimmed) return;
    if (isIframeEmbed(trimmed)) {
      const src = extractIframeSrc(trimmed);
      onUpdateVideo(src || trimmed, 'url');
    } else {
      onUpdateVideo(trimmed, 'url');
    }
  };

  const handleAddImageUrl = () => {
    if (!tempImageUrl.trim()) return;
    if (!coverImage) {
      onUpdateCover(tempImageUrl.trim());
    }
    onUpdateGallery([...galleryImages, tempImageUrl.trim()]);
    setTempImageUrl('');
  };

  const handleRemovePhoto = (index: number) => {
    const updated = [...galleryImages];
    const removed = updated.splice(index, 1);
    onUpdateGallery(updated);

    // If removed item was cover image, update cover
    if (coverImage === removed[0]) {
      onUpdateCover(updated[0] || '');
    }
  };

  const handleSetAsCover = (url: string) => {
    onUpdateCover(url);
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <div>
          <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
            <ImageIcon className="w-4 h-4 text-emerald-600" />
            <span>Media Slider Produk & Layanan</span>
          </h4>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Upload beberapa foto dan 1 video. Foto otomatis dikompres ke format WebP ringan agar website cepat dibuka dan hemat kuota/database.
          </p>
        </div>
      </div>

      {compressStats && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{compressStats}</span>
        </div>
      )}

      {/* 1. PHOTO SECTION */}
      <div>
        <label className="text-xs font-bold text-slate-700 block mb-2">
          Foto Galeri (Bisa Banyak Foto):
        </label>

        {/* Existing photos preview grid */}
        {galleryImages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {galleryImages.map((img, idx) => {
              const isCover = coverImage === img;
              return (
                <div
                  key={idx}
                  className={`relative aspect-4/3 rounded-xl overflow-hidden border-2 bg-slate-100 group ${
                    isCover ? 'border-emerald-600 ring-2 ring-emerald-400/40' : 'border-slate-200'
                  }`}
                >
                  <img src={img} alt={`preview-${idx}`} className="w-full h-full object-cover" />

                  {isCover && (
                    <span className="absolute top-1.5 left-1.5 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow-xs">
                      Foto Utama
                    </span>
                  )}

                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 p-2">
                    {!isCover && (
                      <button
                        type="button"
                        onClick={() => handleSetAsCover(img)}
                        className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-900 rounded text-[10px] font-bold"
                        title="Jadikan Foto Utama"
                      >
                        Pilih Cover
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(idx)}
                      className="p-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-[10px]"
                      title="Hapus Foto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <label className="w-full sm:w-auto px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors shadow-xs">
            <Upload className="w-4 h-4 text-emerald-400" />
            <span>{isCompressing ? 'Mengompres Foto...' : '+ Upload Foto Dari Komputer'}</span>
            <input
              type="file"
              multiple
              accept="image/*"
              disabled={isCompressing}
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </label>

          <span className="text-xs text-slate-400">atau</span>

          <div className="flex-1 w-full flex items-center gap-2">
            <input
              type="url"
              placeholder="Tempel URL Gambar eksternal (https://...)"
              value={tempImageUrl}
              onChange={(e) => setTempImageUrl(e.target.value)}
              className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl"
            />
            <button
              type="button"
              onClick={handleAddImageUrl}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl"
            >
              Tambah
            </button>
          </div>
        </div>
      </div>

      {/* 2. VIDEO SECTION (1 Video Supported) */}
      <div className="pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
            <Film className="w-4 h-4 text-purple-600" />
            <span>Video Produk / Dokumentasi (1 Video di Slider):</span>
          </label>

          <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg text-[11px] font-bold">
            <button
              type="button"
              onClick={() => setVideoInputMode('upload')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                videoInputMode === 'upload' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500'
              }`}
            >
              Upload Komputer
            </button>
            <button
              type="button"
              onClick={() => setVideoInputMode('embed')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                videoInputMode === 'embed' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500'
              }`}
            >
              Embed Code
            </button>
            <button
              type="button"
              onClick={() => setVideoInputMode('youtube')}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                videoInputMode === 'youtube' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500'
              }`}
            >
              YouTube / URL
            </button>
          </div>
        </div>

        {videoUrl ? (
          <div className="p-3 bg-purple-50 rounded-xl border border-purple-200 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0">
                <Play className="w-4 h-4 fill-white" />
              </div>
              <div className="truncate">
                <span className="text-xs font-bold text-purple-950 block truncate">
                  {videoType === 'youtube' ? 'Video YouTube Terpasang' : 'File Video / Embed Terpasang'}
                </span>
                <span className="text-[10px] text-purple-700 font-mono block truncate">{videoUrl.slice(0, 70)}...</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onUpdateVideo('', 'file')}
              className="p-1.5 text-rose-600 hover:bg-rose-100 rounded-lg shrink-0"
              title="Hapus Video"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {videoInputMode === 'upload' && (
              <div>
                <label className="flex flex-col items-center justify-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 border-2 border-dashed border-slate-300 hover:border-purple-500 rounded-xl text-xs font-bold text-slate-600 cursor-pointer transition-colors">
                  <div className="flex items-center gap-2">
                    {isUploadingVideo ? (
                      <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5 text-purple-600" />
                    )}
                    <span>
                      {isUploadingVideo
                        ? `Mengunggah Video ke Server (${videoUploadProgress}%)...`
                        : 'Pilih File Video MP4/WebM/MOV Dari Komputer (Maks 200MB)'}
                    </span>
                  </div>
                  {isUploadingVideo && (
                    <div className="w-full max-w-xs bg-slate-200 h-2 rounded-full overflow-hidden mt-1">
                      <div
                        className="bg-purple-600 h-full transition-all duration-200"
                        style={{ width: `${videoUploadProgress}%` }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime,.mov,.mkv"
                    disabled={isUploadingVideo}
                    onChange={handleVideoFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            )}

            {videoInputMode === 'embed' && (
              <div className="space-y-2">
                <textarea
                  rows={3}
                  placeholder="Tempel kode <iframe>...</iframe> di sini dari YouTube, Vimeo, Google Drive, atau penyedia video lainnya..."
                  value={tempVideoEmbed}
                  onChange={(e) => setTempVideoEmbed(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono leading-relaxed focus:bg-white"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500">
                    Contoh: &lt;iframe src="https://..." ...&gt;&lt;/iframe&gt;
                  </span>
                  <button
                    type="button"
                    onClick={handleSaveEmbed}
                    disabled={!tempVideoEmbed.trim()}
                    className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl cursor-pointer"
                  >
                    Terapkan Embed Code
                  </button>
                </div>
              </div>
            )}

            {videoInputMode === 'youtube' && (
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  placeholder="Masukkan link YouTube (contoh: https://www.youtube.com/watch?v=... atau https://youtu.be/...)"
                  value={tempVideoYoutube}
                  onChange={(e) => setTempVideoYoutube(e.target.value)}
                  className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (tempVideoYoutube.trim()) {
                      onUpdateVideo(tempVideoYoutube.trim(), 'youtube');
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl"
                >
                  Simpan Video
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
